import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { coursesApi } from '../services/api';
import { 
  BookOpen, 
  Play, 
  Clock, 
  Award, 
  TrendingUp,
  Users,
  Target,
  Calendar,
  ArrowRight
} from 'lucide-react';

interface Course {
  id: string;
  title: string;
  description: string;
  thumbnail?: string;
  progress?: number;
  category: {
    name: string;
  };
  price: number;
  duration: number;
  studentsCount: number;
  isEnrolled?: boolean;
}

interface Stats {
  enrolledCourses: number;
  completedCourses: number;
  totalHours: number;
  certificates: number;
}

const DashboardPage: React.FC = () => {
  const { student } = useAuth();
  const [enrolledCourses, setEnrolledCourses] = useState<Course[]>([]);
  const [availableCourses, setAvailableCourses] = useState<Course[]>([]);
  const [stats, setStats] = useState<Stats>({
    enrolledCourses: 0,
    completedCourses: 0,
    totalHours: 0,
    certificates: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      console.log('=== LOADING DASHBOARD DATA ===');
      
      // Cargar cursos inscritos
      console.log('Fetching enrolled courses...');
      const enrolledResponse = await coursesApi.getEnrolled();
      console.log('Enrolled response:', enrolledResponse.data);
      const enrolled = enrolledResponse.data.data?.courses || [];
      console.log('Enrolled courses:', enrolled);
      setEnrolledCourses(enrolled);

      // Cargar cursos disponibles
      const availableResponse = await coursesApi.getAll();
      const available = availableResponse.data.data?.courses || [];
      // Filtrar solo cursos no inscritos
      const notEnrolled = available.filter((course: Course) => 
        !enrolled.find((enrolledCourse: Course) => enrolledCourse.id === course.id)
      );
      setAvailableCourses(notEnrolled.slice(0, 4)); // Solo mostrar 4

      // Calcular estadísticas
      const completedCourses = enrolled.filter((course: Course) => course.progress === 100).length;
      const totalHours = enrolled.reduce((total: number, course: Course) => total + (course.duration || 0), 0);
      
      setStats({
        enrolledCourses: enrolled.length,
        completedCourses,
        totalHours: Math.round(totalHours / 60), // Convertir a horas
        certificates: completedCourses
      });

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      {/* Welcome Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600/20 via-purple-600/10 to-cyan-600/20 dark:from-blue-600/20 dark:via-purple-600/10 dark:to-cyan-600/20 backdrop-blur-sm rounded-3xl p-8 border border-gray-300 dark:border-white/10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full -translate-y-32 translate-x-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-500/10 rounded-full translate-y-24 -translate-x-24"></div>
        
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 to-blue-600 dark:from-white dark:to-blue-200 bg-clip-text text-transparent mb-3">
                ¡Bienvenido de vuelta, {student?.firstName}! 
              </h1>
              <p className="text-gray-600 dark:text-slate-300 text-lg max-w-2xl">
                Tu plataforma de aprendizaje en Inteligencia Artificial está lista. 
                Continúa desarrollando tus habilidades del futuro.
              </p>
            </div>
            <div className="hidden md:flex flex-col items-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mb-2 shadow-lg shadow-blue-500/25">
                <Award size={32} className="text-white" />
              </div>
              <span className="text-xs text-gray-500 dark:text-slate-400 font-medium">Elite Student</span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-200/80 dark:bg-white/10 rounded-full border border-gray-300 dark:border-white/20">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-700 dark:text-slate-200 font-medium">Online</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-200/80 dark:bg-white/10 rounded-full border border-gray-300 dark:border-white/20">
              <Calendar size={16} className="text-blue-500 dark:text-blue-400" />
              <span className="text-sm text-gray-700 dark:text-slate-200">
                {new Date().toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="relative group">
          <div className="bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-600/20 dark:to-blue-800/10 backdrop-blur-xl rounded-2xl p-6 border border-blue-300 dark:border-blue-500/20 shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-blue-200 dark:bg-blue-500/20 rounded-xl border border-blue-300 dark:border-blue-500/30">
                <BookOpen className="text-blue-700 dark:text-blue-300" size={24} />
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-800 dark:text-white">{stats.enrolledCourses}</p>
                <p className="text-sm text-blue-600 dark:text-blue-200 font-medium mt-1">Cursos Activos</p>
              </div>
            </div>
            <div className="h-1 bg-blue-200 dark:bg-blue-500/20 rounded-full">
              <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-300 rounded-full w-3/4"></div>
            </div>
          </div>
        </div>

        <div className="relative group">
          <div className="bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-600/20 dark:to-emerald-800/10 backdrop-blur-xl rounded-2xl p-6 border border-emerald-300 dark:border-emerald-500/20 shadow-lg shadow-emerald-500/10 hover:shadow-emerald-500/20 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-emerald-200 dark:bg-emerald-500/20 rounded-xl border border-emerald-300 dark:border-emerald-500/30">
                <Award className="text-emerald-700 dark:text-emerald-300" size={24} />
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-800 dark:text-white">{stats.completedCourses}</p>
                <p className="text-sm text-emerald-600 dark:text-emerald-200 font-medium mt-1">Completados</p>
              </div>
            </div>
            <div className="h-1 bg-emerald-200 dark:bg-emerald-500/20 rounded-full">
              <div className="h-1 bg-gradient-to-r from-emerald-500 to-emerald-300 rounded-full w-4/5"></div>
            </div>
          </div>
        </div>

        <div className="relative group">
          <div className="bg-gradient-to-br from-cyan-100 to-cyan-200 dark:from-cyan-600/20 dark:to-cyan-800/10 backdrop-blur-xl rounded-2xl p-6 border border-cyan-300 dark:border-cyan-500/20 shadow-lg shadow-cyan-500/10 hover:shadow-cyan-500/20 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-cyan-200 dark:bg-cyan-500/20 rounded-xl border border-cyan-300 dark:border-cyan-500/30">
                <Clock className="text-cyan-700 dark:text-cyan-300" size={24} />
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-800 dark:text-white">{stats.totalHours}<span className="text-xl text-cyan-600 dark:text-cyan-200">h</span></p>
                <p className="text-sm text-cyan-600 dark:text-cyan-200 font-medium mt-1">Tiempo Invertido</p>
              </div>
            </div>
            <div className="h-1 bg-cyan-200 dark:bg-cyan-500/20 rounded-full">
              <div className="h-1 bg-gradient-to-r from-cyan-500 to-cyan-300 rounded-full w-2/3"></div>
            </div>
          </div>
        </div>

        <div className="relative group">
          <div className="bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-600/20 dark:to-purple-800/10 backdrop-blur-xl rounded-2xl p-6 border border-purple-300 dark:border-purple-500/20 shadow-lg shadow-purple-500/10 hover:shadow-purple-500/20 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-purple-200 dark:bg-purple-500/20 rounded-xl border border-purple-300 dark:border-purple-500/30">
                <TrendingUp className="text-purple-700 dark:text-purple-300" size={24} />
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-800 dark:text-white">{stats.certificates}</p>
                <p className="text-sm text-purple-600 dark:text-purple-200 font-medium mt-1">Certificados</p>
              </div>
            </div>
            <div className="h-1 bg-purple-200 dark:bg-purple-500/20 rounded-full">
              <div className="h-1 bg-gradient-to-r from-purple-500 to-purple-300 rounded-full w-1/2"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Cursos En Progreso */}
        <div className="cyber-container">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-200 dark:bg-blue-500/20 rounded-lg">
                <BookOpen className="text-blue-600 dark:text-blue-400" size={20} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Mis Cursos</h2>
            </div>
            <Link 
              to="/courses" 
              className="flex items-center gap-2 text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 text-sm font-semibold transition-colors duration-200 hover:underline"
            >
              Ver todos <ArrowRight size={16} />
            </Link>
          </div>

          <div className="space-y-4">
            {enrolledCourses.length > 0 ? (
              enrolledCourses.slice(0, 3).map((course) => (
                <Link
                  key={course.id}
                  to={`/course/${course.id}`}
                  className="group block p-6 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-slate-800/50 dark:to-slate-700/30 rounded-2xl border border-gray-300 dark:border-slate-600/30 hover:border-blue-500/50 transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-blue-500/10"
                >
                  <div className="flex items-start gap-5">
                    <div className="relative">
                      <div className="w-18 h-18 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-all duration-300">
                        <Play className="text-white" size={24} />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-gray-200 dark:border-slate-800"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-800 dark:text-white mb-2 text-lg group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors duration-200">
                        {course.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-slate-300 mb-4 flex items-center gap-2">
                        <Target size={14} className="text-blue-500 dark:text-blue-400" />
                        {course.category?.name || 'Sin categoría'}
                      </p>
                      
                      {/* Progress Bar */}
                      <div className="relative mb-3">
                        <div className="h-2 bg-gray-300 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-500"
                            style={{ width: `${course.progress || 0}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-xs text-gray-500 dark:text-slate-400 font-medium">
                          {course.progress || 0}% completado
                        </p>
                        <span className="text-xs text-blue-500 dark:text-blue-400 font-semibold flex items-center gap-1">
                          Continuar <ArrowRight size={12} />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gradient-to-br from-gray-300 to-gray-400 dark:from-slate-700 dark:to-slate-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="text-gray-600 dark:text-slate-400" size={32} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">No hay cursos activos</h3>
                <p className="text-gray-600 dark:text-slate-400 mb-4">
                  Explora nuestra biblioteca de cursos de IA
                </p>
                <Link 
                  to="/courses"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-cyan-600 transition-all duration-200"
                >
                  Explorar Cursos <ArrowRight size={16} />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Cursos Recomendados */}
        <div className="cyber-container">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Cursos Recomendados</h2>
            <Link 
              to="/courses" 
              className="text-cyan-500 dark:text-cyan-400 hover:text-cyan-600 dark:hover:text-cyan-300 text-sm font-medium flex items-center gap-1"
            >
              Ver todos <ArrowRight size={16} />
            </Link>
          </div>

          <div className="space-y-4">
            {availableCourses.length > 0 ? (
              availableCourses.slice(0, 3).map((course) => (
                <div
                  key={course.id}
                  className="p-4 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-transparent"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                      <Target className="text-white" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 dark:text-white mb-1 truncate">
                        {course.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                        {course.category?.name || 'Sin categoría'}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-400 dark:text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {Math.ceil(course.duration / 60)} semanas
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {course.studentsCount} estudiantes
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-lg font-bold text-cyan-500 dark:text-cyan-400">
                          {course.price > 0 ? `$${course.price.toLocaleString()}` : 'Gratis'}
                        </div>
                        <Link
                          to={course.price > 0 ? `/payment/${course.id}` : `/course/${course.id}`}
                          className="cyber-button-secondary text-xs px-3 py-1"
                        >
                          {course.price > 0 ? 'Inscribirse' : 'Acceder'}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Target className="mx-auto text-gray-400 dark:text-gray-500 mb-3" size={48} />
                <p className="text-gray-500 dark:text-gray-400">
                  No hay cursos disponibles en este momento.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actividad Reciente */}
      <div className="cyber-container">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">Actividad Reciente</h2>
        
        <div className="space-y-4">
          {enrolledCourses.slice(0, 3).map((course, index) => (
            <div key={course.id} className="flex items-center gap-4 p-3 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-transparent">
              <div className="p-2 bg-blue-200 dark:bg-blue-500/20 rounded-lg">
                <Calendar className="text-blue-600 dark:text-blue-400" size={16} />
              </div>
              <div className="flex-1">
                <p className="text-gray-800 dark:text-white text-sm">
                  Progreso en <span className="font-semibold">{course.title}</span>
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-xs">
                  Hace {index + 1} día{index > 0 ? 's' : ''}
                </p>
              </div>
              <div className="text-cyan-500 dark:text-cyan-400 font-semibold text-sm">
                {course.progress || 0}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;